import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { BusinessCaseButton } from "@/components/site/BusinessCaseButton";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LicenseCheckout } from "@/components/LicenseCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { getStripeEnvironment } from "@/lib/stripe";
import { useSession } from "@/hooks/useSession";
import { getCheckoutSummary, type CheckoutSummary } from "@/utils/payments.functions";
import { PRICE_KEYS } from "@/lib/products";
import pricingPhotoSrc from "@/assets/pricing-hero.jpg";
const pricingPhoto = { url: pricingPhotoSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
import { SITE_URL } from "@/lib/seo";
const boardroomAsset = { url: boardroomAssetSrc };

export const Route = createFileRoute("/purchase")({
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/purchase/` }],
    meta: [
      { title: "Get Started | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Buy annual Leadership Edge Live licenses. $349 per person per year for unlimited courses, plus optional coaching at $499 per person per year.",
      },
      { property: "og:title", content: "Get Started | Leadership Edge Live" },
      {
        property: "og:description",
        content: "Annual licenses at $349 per person. Unlimited courses. Optional coaching add-on.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Purchase,
});

const BASE_PER_PERSON = 349;
const COACHING_PER_PERSON = 499;
const LICENSE_PRICE_ID = PRICE_KEYS.businessUnlimited;
const COACHING_PRICE_ID = PRICE_KEYS.businessCoaching;

const included = [
  {
    title: "Unlimited courses",
    body: "Every live course, every session, all year. No per-seat course fees to track.",
  },
  {
    title: "One price per person",
    body: "$349 per person per year. Enroll the people you're developing; register them as often as you want.",
  },
  {
    title: "Optional coaching",
    body: "Add unlimited asynchronous coaching for $499 per person per year. Add it for some people, not all.",
  },
  {
    title: "Nothing to administer",
    body: "No LMS to run, no facilitators to book, no curriculum to write. We handle the delivery.",
  },
];

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
const fromCents = (cents: number, currency: string | null) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency ?? "usd").toUpperCase(),
  }).format(cents / 100);

function Purchase() {
  const { session_id: sessionId } = Route.useSearch();

  if (sessionId) return <Confirmation sessionId={sessionId} />;
  return <PurchaseForm />;
}

function Confirmation({ sessionId }: { sessionId: string }) {
  const [summary, setSummary] = useState<CheckoutSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getCheckoutSummary({
          data: { sessionId, environment: getStripeEnvironment() },
        });
        if (cancelled) return;
        if ("error" in result) setError(result.error);
        else setSummary(result.summary);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load your order.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = summary?.paymentStatus !== "unpaid";

  return (
    <>
      <PaymentTestModeBanner />
      <PageHero
        maxWidth="6xl"
        headline={
          <>
            Thanks. <span className="text-[#693D94]">You're in.</span>
          </>
        }
        supporting="Check your inbox. Your setup email has the link to assign seats and invite your managers."
        ctas={[
          { label: "Browse courses", to: "/course-catalog/", variant: "primary" },
        ]}
      />
      <Section tone="default">
        <SectionHeader
          title="What you bought"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <div className="max-w-xl rounded-2xl border border-border/60 bg-background p-7">
          {error ? (
            <p className="text-sm text-muted-foreground">
              Your payment went through, but we couldn't load the receipt here. Check your email for
              confirmation.
            </p>
          ) : !summary ? (
            <p className="text-sm text-muted-foreground">Loading your order...</p>
          ) : (
            <dl className="space-y-3 text-sm">
              {summary.lines.map((line, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground">
                    {line.description}
                    {line.quantity > 1 ? ` x ${line.quantity}` : ""}
                  </dt>
                  <dd className="font-semibold text-ink">
                    {fromCents(line.amount, summary.currency)}
                  </dd>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-4 border-t border-border/60 pt-3">
                <dt className="font-semibold text-ink">Total per year</dt>
                <dd className="font-semibold text-ink">
                  {fromCents(summary.amountTotal ?? 0, summary.currency)}
                </dd>
              </div>
            </dl>
          )}
          {summary && !paid ? (
            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              Your payment is still settling. We'll email you as soon as it clears.
            </p>
          ) : null}
        </div>
      </Section>
    </>
  );
}

function PurchaseForm() {
  const [seats, setSeats] = useState(1);
  const [coachingOn, setCoachingOn] = useState(false);
  const [coachingSeats, setCoachingSeats] = useState(1);
  const { user } = useSession();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [returnUrl, setReturnUrl] = useState("");

  const coachingCount = coachingOn ? Math.min(coachingSeats, seats) : 0;
  const baseTotal = seats * BASE_PER_PERSON;
  const coachingTotal = coachingCount * COACHING_PER_PERSON;
  const total = baseTotal + coachingTotal;

  const items = [
    { priceId: LICENSE_PRICE_ID, quantity: seats },
    ...(coachingCount > 0 ? [{ priceId: COACHING_PRICE_ID, quantity: coachingCount }] : []),
  ];

  if (checkoutOpen && returnUrl) {
    return (
      <>
        <PaymentTestModeBanner />
        <PageHero
          maxWidth="6xl"
          headline={
            <>
              Almost done. <span className="text-[#693D94]">Enter your payment details.</span>
            </>
          }
          supporting={`${seats} ${seats === 1 ? "license" : "licenses"}${
            coachingCount > 0 ? ` plus coaching for ${coachingCount}` : ""
          }, ${usd(total)} per year.`}
          ctas={[]}
        />
        <Section tone="default">
          <div className="rounded-2xl border border-border/60 bg-background p-4 md:p-7">
            <LicenseCheckout
              items={items}
              customerEmail={email || undefined}
              userId={user?.id}
              buyer={{
                fullName: fullName || undefined,
                company: company || undefined,
                phone: phone || undefined,
              }}
              returnUrl={returnUrl}
            />
          </div>
          <button
            type="button"
            onClick={() => setCheckoutOpen(false)}
            className="mt-6 text-sm font-semibold text-primary underline"
          >
            Change your order
          </button>
        </Section>
      </>
    );
  }

  return (
    <>
      <PaymentTestModeBanner />
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={pricingPhoto.url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) 38%, rgba(255,255,255,0) 65%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              One price per person. Unlimited courses.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              <strong className="font-bold">$349 per person per year.</strong> Add unlimited
              asynchronous coaching for $499 per person per year.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact/"
                className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
              >
                Talk to a Development Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Section tone="default">
        <SectionHeader
          title="Your license"
          intro="Tell us how many people you're developing, then pay by card."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setReturnUrl(`${window.location.origin}/purchase?session_id={CHECKOUT_SESSION_ID}`);
            setCheckoutOpen(true);
          }}
          className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]"
        >
          <div className="rounded-2xl border border-border/60 bg-background p-7">
            <div>
              <Label htmlFor="seats" className="text-sm font-semibold text-ink">
                Number of people
              </Label>
              <Input
                id="seats"
                type="number"
                min={1}
                max={5000}
                required
                value={seats}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setSeats(Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), 5000) : 1);
                }}
                className="mt-3 w-32"
              />
              <p className="mt-2 text-sm text-muted-foreground">
                {usd(BASE_PER_PERSON)} per person per year.
              </p>
            </div>

            <div className="mt-8 border-t border-border/60 pt-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="coaching" className="text-sm font-semibold text-ink">
                    Add unlimited asynchronous coaching
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {usd(COACHING_PER_PERSON)} per person per year.
                  </p>
                </div>
                <Switch id="coaching" checked={coachingOn} onCheckedChange={setCoachingOn} />
              </div>
              {coachingOn ? (
                <div className="mt-6">
                  <Label htmlFor="coaching-seats" className="text-sm font-semibold text-ink">
                    People with coaching
                  </Label>
                  <Input
                    id="coaching-seats"
                    type="number"
                    min={1}
                    max={seats}
                    value={coachingCount}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setCoachingSeats(
                        Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), seats) : 1,
                      );
                    }}
                    className="mt-3 w-32"
                  />
                </div>
              ) : null}
            </div>

            <div className="mt-8 grid gap-5 border-t border-border/60 pt-7 md:grid-cols-2">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold text-ink">
                  Your name
                </Label>
                <Input
                  id="name"
                  name="name"
                  required
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-sm font-semibold text-ink">
                  Company
                </Label>
                <Input
                  id="company"
                  name="company"
                  required
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-ink">
                  Work email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold text-ink">
                  Phone, optional
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col rounded-2xl border border-border/60 bg-lavender/60 p-7">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Your order
            </p>
            <p className="mt-3 font-display text-4xl text-ink">{usd(total)}</p>
            <p className="mt-1 text-sm text-muted-foreground">per year</p>

            <dl className="mt-6 space-y-3 border-t border-border/60 pt-6 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">
                  Annual licenses, {seats} {seats === 1 ? "person" : "people"}
                </dt>
                <dd className="font-semibold text-ink">{usd(baseTotal)}</dd>
              </div>
              {coachingCount > 0 ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground">
                    Coaching add-on, {coachingCount} {coachingCount === 1 ? "person" : "people"}
                  </dt>
                  <dd className="font-semibold text-ink">{usd(coachingTotal)}</dd>
                </div>
              ) : null}
            </dl>

            <div className="mt-auto pt-7">
              <button
                type="submit"
                className="inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5"
              >
                Place order
              </button>
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                You'll pay by card on the next step. Tax is calculated at checkout. Questions first?{" "}
                <Link to="/contact/" className="font-semibold text-primary underline">
                  Talk to a Development Pro
                </Link>
                .
              </p>
            </div>
          </div>
        </form>
      </Section>

      <Section tone="lavender">
        <SectionHeader
          title="What's in the price"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <div className="grid gap-6 md:grid-cols-2">
          {included.map((item) => (
            <article
              key={item.title}
              className="rounded-2xl border border-border/60 bg-background p-7 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)]"
            >
              <h3 className="font-display text-lg leading-snug text-ink">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <BusinessCaseButton />
        </div>
      </Section>

      <AdvisorCTA
        title="Want a quote for your group?"
        body="Tell us how many people you're developing. We'll confirm the numbers and the start dates."
        backgroundImage={boardroomAsset.url}
      />
    </>
  );
}
